import reactNativeMessage from 'hooks/useReactNativeMessage';

/**
 * Safely gets the user agent string, returns empty string in SSR/tests.
 *
 * @returns {string} The user agent string or empty string.
 */
const getUserAgent = () => {
  if (typeof window === 'undefined' || !window.navigator) return '';
  return window.navigator.userAgent || '';
};

/**
 * Safely gets maxTouchPoints, returns 0 in SSR/tests.
 *
 * @returns {number} The max touch points or 0.
 */
const getMaxTouchPoints = () => {
  if (typeof window === 'undefined' || !window.navigator) return 0;
  return typeof window.navigator.maxTouchPoints === 'number' ? window.navigator.maxTouchPoints : 0;
};

/**
 * Detects if the current device is an iPad (including iPadOS 13+ which uses Mac user agent).
 *
 * @returns {boolean} True if it's an iPad, false otherwise.
 */
const isIPad = () => {
  const ua = getUserAgent();
  // Classic iPad detection
  if (/iPad/i.test(ua)) return true;
  // iPadOS 13+ detection: Mac user agent but with touch support
  if (/Macintosh/i.test(ua) && getMaxTouchPoints() > 1) {
    return true;
  }
  return false;
};

/**
 * Checks if the current environment is a webview.
 *
 * @returns {boolean} True if it's a webview, false otherwise.
 */
export const isWebView = () => {
  const ua = getUserAgent();
  if (!ua) return false;
  // Classic iOS webview detection (no "Version" in UA)
  if (/((iPhone|iPod|iPad).*AppleWebKit(?!.*Version))/i.test(ua)) return true;
  // iPadOS 13+ webview detection (Mac UA without Safari/Version)
  if (isIPad() && /AppleWebKit/i.test(ua) && !/Version\//i.test(ua)) return true;
  // Android webview detection
  if (/; wv/i.test(ua)) return true;
  return false;
};

/**
 * Checks if the current environment is an iOS webview.
 *
 * @returns {boolean} True if it's an iOS webview, false otherwise.
 */
export const isIOSWebView = () => {
  const ua = getUserAgent();
  if (!ua) return false;
  // Classic iOS webview
  if (/((iPhone|iPod|iPad).*AppleWebKit(?!.*Version))/i.test(ua)) return true;
  // iPadOS 13+ webview
  if (isIPad() && /AppleWebKit/i.test(ua) && !/Version\//i.test(ua)) return true;
  return false;
};

/**
 * Checks if the current environment is an Android webview.
 *
 * @returns {boolean} True if it's an Android webview, false otherwise.
 */
export const isAndroidWebView = () => {
  const ua = getUserAgent();
  return /; wv/i.test(ua);
};

/**
 * Gets the webview type if in a webview environment.
 *
 * @returns {string|null} 'ios', 'android', or null if not in a webview.
 */
export const getWebViewType = () => {
  if (isIOSWebView()) return 'ios';
  if (isAndroidWebView()) return 'android';
  return null;
};

/**
 * Checks if the page is running inside a native app WebView by looking for the
 * authoritative native_app_* identifier appended to the user-agent string by the
 * mobile apps (saas-app / midsize-app). Unlike the heuristic isWebView(), this is
 * not fooled by mobile browsers (e.g. Chrome on iOS) that lack "Version/" in their UA.
 *
 * @returns {boolean} True only when the native_app_* tag is present in the user-agent.
 */
export const isNativeAppWebView = () => {
  const ua = getUserAgent();
  return /native_app_(ios|android)/i.test(ua);
};

/**
 * Checks if the page is running inside an iOS native app WebView.
 * Uses the authoritative native_app_ios identifier, not heuristic UA detection.
 *
 * @returns {boolean} True only when native_app_ios is present in the user-agent.
 */
export const isNativeIOSWebView = () => {
  const ua = getUserAgent();
  return /native_app_ios/i.test(ua);
};

/**
 * Checks if the page is running inside an Android native app WebView.
 * Uses the authoritative native_app_android identifier, not heuristic UA detection.
 *
 * @returns {boolean} True only when native_app_android is present in the user-agent.
 */
export const isNativeAndroidWebView = () => {
  const ua = getUserAgent();
  return /native_app_android/i.test(ua);
};

/**
 * Posts the purchaser identity to the native app at the purchaser step. The native shell owns the
 * OneSignal (APNs/FCM) subscription — the web SDK only identifies the web user — so it calls
 * OneSignal.login(phone) with the same key the web flow writes to `$onesignal_user_id`, letting
 * Mixpanel cohorts reach the device. Fire-and-forget: the post is internally guarded and never
 * blocks the flow that triggered it. No-op outside the native app WebView or without a phone.
 *
 * @param {Object} identity - Purchaser identity payload.
 * @param {string} identity.phone - Purchaser phone (national number digits); the identity key.
 * @param {string} identity.email - Purchaser email.
 * @param {string} identity.firstName - Purchaser first name.
 * @param {string} identity.lastName - Purchaser last name.
 * @returns {void}
 */
export const postNativeIdentifyUser = ({ phone, email, firstName, lastName }) => {
  if (!phone || !isNativeAppWebView()) return;

  reactNativeMessage().post({ type: 'IDENTIFY_USER', phone, email, firstName, lastName });
};
